// Small fixes to the SDP this phone sends.
//
// Asterisk (checked in 20.6 and 20.9.2, res_pjsip_sdp_rtp.c) treats an audio
// stream whose connection address is 0.0.0.0 (or ::) as "put on hold by the
// caller" and plays hold music to the other person for as long as that SDP
// stands, even when the stream says sendrecv and ICE then connects normally.
//
// The browser writes 0.0.0.0 when it has no usable address yet: only its
// hidden "<uuid>.local" host candidate, because STUN/TURN hasn't answered
// within our gathering wait (strict office networks, slow VPNs, TURN over TLS
// only). The call still connects through ICE, so the user hears the other
// person, while the other person hears music for the whole call.
//
// ICE decides where media really goes, so the address only has to be "not
// zero". 127.0.0.1 with the discard port 9 the browser already uses keeps
// anything Asterisk sends before ICE finishes on the server itself.
// A real hold (a=sendonly / a=inactive) is left exactly as it is.

const NULL_C = /^c=IN IP[46] (0\.0\.0\.0|::)\s*$/;
const PLACEHOLDER = 'c=IN IP4 127.0.0.1';

/** Replace a zero connection address in non-hold audio/video sections. */
export function fixNullConnection(sdp: string): string {
  const eol = sdp.includes('\r\n') ? '\r\n' : '\n';
  const lines = sdp.split(/\r?\n/);
  // sections: [session, media1, media2, …] as line index ranges
  const starts = [0];
  lines.forEach((l, i) => {
    if (l.startsWith('m=')) starts.push(i);
  });
  const held = (from: number, to: number) =>
    lines.slice(from, to).some((l) => /^a=(sendonly|inactive)\s*$/.test(l));
  const sections = starts.map((from, k) => ({ from, to: starts[k + 1] ?? lines.length }));
  const media = sections.slice(1);
  let changed = false;

  sections.forEach(({ from, to }, k) => {
    const isSession = k === 0;
    for (let i = from; i < to; i++) {
      if (!NULL_C.test(lines[i]!)) continue;
      // session-level c= applies to every media section without its own c=
      const keep = isSession
        ? media.some((m) => held(m.from, m.to) && !lines.slice(m.from, m.to).some((l) => l.startsWith('c=')))
        : held(from, to);
      if (keep) continue;
      lines[i] = PLACEHOLDER;
      changed = true;
    }
  });
  return changed ? lines.join(eol) : sdp;
}
